import React, { useContext } from "react";
import { QuantityDropdownPriceDisplay as QuantityDropdownPriceDisplayValues } from "../../../types/ProductBuilderTypes";
import { getPricePresentationAsElement, PresentationType } from "../../../services/pricing";
import PriceRenderer from "../../productBuilder/productBuilder/components/PriceRenderer";
import { BffDataContext } from "../../../contexts/BffDataContextProvider";
import { PricingContext } from "../../../contexts/PricingContext";
import { PageStaticDataContext } from "../../../contexts/PageStaticDataContextProvider";

type QuantityDropdownPriceDisplayProps = {
  quantityDropdownPriceDisplay?: QuantityDropdownPriceDisplayValues;
  quantity: number;
};

const QuantityDropdownPriceDisplay = (props: QuantityDropdownPriceDisplayProps) => {
  const { quantityDropdownPriceDisplay, quantity } = props;
  const { prices, compatibleQuantities } = useContext(BffDataContext);
  const { vatInclusivity } = useContext(PricingContext);
  const { locale } = useContext(PageStaticDataContext);

  const clientSideUnitPriceElements =
    prices && compatibleQuantities
      ? compatibleQuantities.reduce((result: Record<number, JSX.Element>, quantity: number) => {
          const priceForQuantity = prices[Number(quantity)];
          if (priceForQuantity) {
            const priceElement = getPricePresentationAsElement({
              price: priceForQuantity,
              quantity,
              locale,
              presentationType: PresentationType.UNIT_PRICING,
              vatInclusivity,
              hideVatMessage: true,
            });
            result[Number(quantity)] = priceElement;
          }
          return result;
        }, {})
      : {};

  const clientSideTotalPriceElements =
    prices && compatibleQuantities
      ? compatibleQuantities.reduce((result: Record<number, JSX.Element>, quantity: number) => {
          const priceForQuantity = prices[Number(quantity)];
          if (priceForQuantity) {
            const priceElement = getPricePresentationAsElement({
              price: priceForQuantity,
              quantity,
              locale,
              presentationType: PresentationType.RAW_PRICE,
              vatInclusivity,
              hideVatMessage: true,
            });
            result[Number(quantity)] = priceElement;
          }
          return result;
        }, {})
      : {};

  const getQuantityDropdownPricingElement = (quantity: number) => {
    switch (quantityDropdownPriceDisplay) {
      case "Total Price": {
        return clientSideTotalPriceElements && clientSideTotalPriceElements[Number(quantity)] ? (
          <>
            &nbsp;(
            {<PriceRenderer pricingElement={clientSideTotalPriceElements[Number(quantity)]} />}
            )&nbsp;
          </>
        ) : (
          <></>
        );
      }
      case "Price per unit":
      case undefined:
      case null: {
        return clientSideUnitPriceElements && clientSideUnitPriceElements[Number(quantity)] ? (
          <>
            &nbsp;(
            {<PriceRenderer pricingElement={clientSideUnitPriceElements[Number(quantity)]} />}
            )&nbsp;
          </>
        ) : (
          <></>
        );
      }
      case "No Price Display":
      default: {
        return <></>;
      }
    }
  };

  return getQuantityDropdownPricingElement(quantity);
};

export default QuantityDropdownPriceDisplay;
